import { useQuery } from '@apollo/client';
import React from 'react';
import { Link } from '../utils/router/v3Bridge';
import useAppSelector from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import { PostType } from '../graphql/generated/graphql';
import unarchiveIcon from '../icons/non-prefilled/16x16/unarchive.svg';
import userPath from '../utils/url/userPath';
import { getStateIcon } from './PostIcon';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import PostViewStateBar from './PostViewStateBar';
import useToggleArchived from './useToggleArchived';
import { gql } from '../graphql/generated/gql';
import type { PostStateBarComponentProps } from './PostStateBar';

const POST_QUERY = gql(`
  query PostArchivedBar($id: ID!) {
    post(id: $id) {
      id
      type
      publishedAt
      archivedAt
      archiver {
        id
        name
        ...userPath_user
      }
      ...usePostPolicyCheck_post
      ...useToggleArchived_post
    }
  }
`);

const getName = (isTemplate: boolean, isDraft: boolean) => {
  if (isTemplate) {
    return isDraft ? 'Draft Template' : 'Template';
  }
  return isDraft ? 'Draft' : 'Archived';
};

const PostArchivedBar = ({ postId, compact }: PostStateBarComponentProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const currentUser = useAppSelector((state) => state.session.user);
  const toggleArchived = useToggleArchived();
  const { data } = useQuery(POST_QUERY, { variables: { id: postId } });
  const post = data?.post;

  if (!post?.archiver) return null;

  const isTemplate = post ? post.type === PostType.Template : false;
  const isDraft = post ? !post.publishedAt : false;

  return (
    <PostViewStateBar
      actions={
        postPolicyCheck(PostPolicyAction.EditPost, post, currentUser) && (
          <PostStateBarOverflowMenu variant="muted">
            <MenuItem icon={unarchiveIcon} onClick={() => toggleArchived(post)}>
              Unarchive
            </MenuItem>
          </PostStateBarOverflowMenu>
        )
      }
    >
      <SVGIcon>
        {getStateIcon({
          type: post.type,
          draft: isDraft,
          archived: true,
        })}
      </SVGIcon>
      <span>
        <strong>{getName(isTemplate, isDraft)}</strong>
        {!compact && (
          <>
            {isTemplate || isDraft ? ' archived by ' : ' by '}
            <Link to={userPath(post.archiver)}>
              {currentUser.id === post.archiver.id ? 'you' : post.archiver.name}
            </Link>{' '}
            <Timestamp date={post.archivedAt} />
          </>
        )}
      </span>
    </PostViewStateBar>
  );
};

export default PostArchivedBar;
